import whopImg from "@/assets/case-studies/whop.jpg";
import c23Img from "@/assets/case-studies/c23.png";
import empireImg from "@/assets/case-studies/empire.jpg";
import hummingImg from "@/assets/case-studies/humming.png";
import merImg from "@/assets/case-studies/mer.jpg";
import chillImg from "@/assets/case-studies/chill.jpg";
import exceptionalImg from "@/assets/case-studies/exceptional.jpg";

import whopLogo from "@/assets/case-studies/whop-logo.svg";
import c23Logo from "@/assets/case-studies/c23-logo.svg";
import empireLogo from "@/assets/case-studies/empire-logo.png";
import hummingLogo from "@/assets/case-studies/humming-logo.svg";
import merLogo from "@/assets/case-studies/mer-logo.svg";
import chillLogo from "@/assets/case-studies/chill-logo.svg";
import exceptionalLogo from "@/assets/case-studies/exceptional-logo.png";

import svcWebDesignLight from "@/assets/services/web-design-light.svg";
import svcWebDesignDark from "@/assets/services/web-design-dark.svg";
import svcEcommerceLight from "@/assets/services/framer-ecommerce-light.svg";
import svcEcommerceDark from "@/assets/services/framer-ecommerce-dark.svg";
import svcWebsiteFixLight from "@/assets/services/website-fix-light.svg";
import svcWebsiteFixDark from "@/assets/services/website-fix-dark.svg";
import svcEmailMarketingLight from "@/assets/services/email-marketing-light.svg";
import svcEmailMarketingDark from "@/assets/services/email-marketing-dark.svg";
import svcAssessmentLight from "@/assets/services/assessment-light.svg";
import svcAssessmentDark from "@/assets/services/assessment-dark.svg";

export interface CaseStudy {
  id: string;
  title: string;
  subtitle: string;
  category: string;
  image: string;
  logo: string;
  url?: string;
}

export const caseStudies: CaseStudy[] = [
  {
    id: "whop",
    title: "Whop HigherEd",
    subtitle: "Building Entrepreneurs",
    category: "Framer Web Dev",
    image: whopImg,
    logo: whopLogo,
  },
  {
    id: "c23",
    title: "c23 Labs",
    subtitle: "Private Tattoo Mentorship",
    category: "Framer Web Dev",
    image: c23Img,
    logo: c23Logo,
  },
  {
    id: "empire",
    title: "EMPIRE",
    subtitle: "Independent Record Label",
    category: "Framer Commerce Web Dev",
    image: empireImg,
    logo: empireLogo,
  },
  {
    id: "humming",
    title: "Humming.ai",
    subtitle: "Premium Ad Services",
    category: "Framer Web Dev",
    image: hummingImg,
    logo: hummingLogo,
  },
  {
    id: "mer",
    title: "MĒR",
    subtitle: "Food & Beverage",
    category: "Framer Template Development",
    image: merImg,
    logo: merLogo,
  },
  {
    id: "chill",
    title: "Chill Bites & Drinks",
    subtitle: "Beverage Brand",
    category: "Framer Web Dev + Branding",
    image: chillImg,
    logo: chillLogo,
  },
  {
    id: "exceptional",
    title: "Exceptional 100",
    subtitle: "Annual Publication",
    category: "Framer Site Development",
    image: exceptionalImg,
    logo: exceptionalLogo,
  },
];

export interface Service {
  title: string;
  description: string;
  pricing: string;
  details?: string[];
  image: string;
  imageDark?: string;
}

export const services: Service[] = [
  {
    title: "Framer Website Design & Development",
    description:
      "We design and build structured, UX-first websites in Framer. The work starts with alignment, not aesthetics — we map your sitemap, clean up page hierarchy, restructure slugs if needed, and plan how content will grow before designing a single screen. Most builds include 5–10 custom pages, responsive development, and a CMS setup your team can actually manage without calling us every week.",
    pricing: "Starting at $5,000*",
    image: svcWebDesignLight,
    imageDark: svcWebDesignDark,
    details: [
      "Strategy and sitemap planning",
      "Custom page design",
      "Framer development with CMS setup",
      "On-site SEO structure",
      "Analytics integration",
    ],
  },
  {
    title: "Framer E-Commerce Development",
    description:
      "E-comm solutions built in Framer utilizing Framer Commerce and Frameship to create a custom frontend storefront experience while Shopify runs the show on the backend. We focus on store logic — product structure, collection hierarchy, conditional displays, and CMS alignment. The goal is a storefront that feels editorial but functions predictably behind the scenes.",
    pricing: "base + $2,500*",
    image: svcEcommerceLight,
    imageDark: svcEcommerceDark,
    details: [
      "Shopify store architecture review",
      "Product and collection restructuring - optional",
      "Custom-designed storefront or redesign",
      "Shopify to Framer integration (Framer Commerce / Frameship)",
      "CMS-connected storefront build",
    ],
  },
  {
    title: "Framer Website Fix",
    description:
      "Not every problem requires a rebuild. We step in when something feels off but you can't pinpoint why. We audit the structure, identify where breakdowns are happening, and implement targeted fixes — whether that's performance tuning or reworking your CMS so your team can update content without breaking the layout.",
    pricing: "Contact for Pricing",
    image: svcWebsiteFixLight,
    imageDark: svcWebsiteFixDark,
    details: [
      "Bug fixes & troubleshooting",
      "Performance optimization",
      "CMS restructuring",
      "Content updates & adjustments",
    ],
  },
  {
    title: "Email Marketing Design · Klaviyo",
    description:
      "Email should not feel like a separate department from your website. We look at how people move through your site first — what they click, where they hesitate, what they abandon. Then we structure flows around that reality. Inside Klaviyo, we clean up what's messy, build modular templates your team can reuse, and set up flows that make sense.",
    pricing: "Starting at $1,200/mo",
    image: svcEmailMarketingLight,
    imageDark: svcEmailMarketingDark,
    details: [
      "Reworking overlapping or redundant automations",
      "Building clear flow logic tied to user behavior",
      "Modular template design",
      "Connecting email performance back to storefront behavior",
    ],
  },
  {
    title: "Website Design Assessment",
    description:
      "This is for when you know something's wrong — either from intuition or quarterly reports. We review your site as a user and as someone who understands systems. You get a written breakdown of what's breaking down, why it's happening, what to fix now, and what can wait.",
    pricing: "Contact for Pricing",
    image: svcAssessmentLight,
    imageDark: svcAssessmentDark,
    details: [
      "Page hierarchy and navigation audit",
      "CMS usability review",
      "Accessibility gap analysis",
      "Prioritized improvement roadmap",
    ],
  },
];

export interface Testimonial {
  name: string;
  company: string;
  text: string;
  date: string;
}

export const testimonials: Testimonial[] = [
  {
    name: "Alli Minetti Labs",
    company: "Contra Labs",
    text: "Great collaboration with Madison! Great experience with reliable communication, fast iteration, and implementing feedback quickly.",
    date: "Feb 2026",
  },
  {
    name: "Elaina Olson",
    company: "Looped.IN",
    text: "Excellent work, great communication, and nice to work with!",
    date: "Nov 2025",
  },
  {
    name: "Melissa Aurigemma",
    company: "Exceptional Capital",
    text: "Madison is amazing! Second year working on an annual project with her and she executed perfectly.",
    date: "Sep 2025",
  },
  {
    name: "Jasmine Lee",
    company: "Base",
    text: "Madison was phenomenal to work with - great at her job, eager to hop in and suggest changes, responsive and a great communicator.",
    date: "Aug 2024",
  },
  {
    name: "Oscar Akermo",
    company: "",
    text: "Madison is as flexible as she is thorough. Her taste and knowledge in what looks good is impeccable.",
    date: "Dec 2024",
  },
  {
    name: "Mary Petrino",
    company: "Whop",
    text: "Madison is lovely to work with! She meets prompt deadlines and is extremely timely with any Framer request that I have.",
    date: "Jan 2025",
  },
];
